import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Trash2, UserPlus, Check, ChevronsUpDown } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { Profile } from '@/types/supabase';

type ChannelMember = {
  user_id: string;
  profiles: {
    full_name: string | null;
    avatar_url: string | null;
  } | null;
};

interface ChannelManagementCardProps {
  channelSlug: string;
  channelName: string;
  channelDescription: string;
  allUsers: Profile[];
}

const ChannelManagementCard: React.FC<ChannelManagementCardProps> = ({ channelSlug, channelName, channelDescription, allUsers }) => {
  const { toast } = useToast();
  const [channelId, setChannelId] = useState<string | null>(null);
  const [members, setMembers] = useState<ChannelMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<Profile | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [showUserList, setShowUserList] = useState(false);

  const fetchChannelData = useCallback(async () => {
    const { data, error } = await supabase
      .rpc('admin_get_channel_by_slug', { p_slug: channelSlug })
      .single();

    if (error || !data) {
      console.error(`Error fetching ${channelSlug} channel:`, error);
      toast({ title: 'Error', description: `Could not find ${channelName} channel.`, variant: 'destructive' });
      return null;
    }
    return data;
  }, [channelSlug, channelName, toast]);

  const fetchChannelMembers = useCallback(async (id: string) => {
    const { data, error } = await supabase.from('lobby_channel_members').select('user_id, profiles(id, full_name, avatar_url)').eq('channel_id', id);
    if (error) {
      toast({ title: 'Error', description: `Could not fetch ${channelName} channel members.`, variant: 'destructive' });
      return [];
    }
    return data as any[];
  }, [channelName, toast]);

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      const data = await fetchChannelData();
      if (data) {
        setChannelId(data.id);
        setShowUserList(data.show_user_list);
        const channelMembersData = await fetchChannelMembers(data.id);
        setMembers(channelMembersData);
      }
      setLoading(false);
    };
    initialize();
  }, [fetchChannelData, fetchChannelMembers]);

  const handleAddUser = async () => {
    if (!selectedUser || !channelId) return;
    if (members.some(m => m.user_id === selectedUser.id)) {
      toast({ title: 'User already in channel', variant: 'default' });
      return;
    }
    const { error } = await supabase.rpc('admin_add_lobby_channel_member', { p_channel_id: channelId, p_user_id: selectedUser.id });
    if (error) {
      toast({ title: 'Error', description: `Failed to add user: ${error.message}`, variant: 'destructive' });
    } else {
      toast({ title: 'Success', description: `User added to ${channelName} channel.` });
      setMembers(await fetchChannelMembers(channelId));
      setSelectedUser(null);
      setPopoverOpen(false);
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!channelId) return;
    const { error } = await supabase.from('lobby_channel_members').delete().match({ channel_id: channelId, user_id: userId });
    if (error) {
      toast({ title: 'Error', description: `Failed to remove user: ${error.message}`, variant: 'destructive' });
    } else {
      toast({ title: 'Success', description: `User removed from ${channelName} channel.` });
      setMembers(members.filter(m => m.user_id !== userId));
    }
  };

  const handleToggleUserList = async (enabled: boolean) => {
    if (!channelId) return;
    setShowUserList(enabled);
    const { error } = await supabase.from('lobby_channels').update({ show_user_list: enabled }).eq('id', channelId);
    if (error) {
      setShowUserList(!enabled);
      toast({ title: 'Error', description: 'Failed to update setting.', variant: 'destructive' });
    } else {
      toast({ title: 'Success', description: 'Setting updated.' });
    }
  };

  if (loading) return <div className="flex justify-center items-center p-8"><Loader2 className="animate-spin" /></div>;
  if (!channelId) return <p className="p-4 text-muted-foreground">Could not load settings for the #{channelSlug} channel.</p>;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{channelName} Channel Access</CardTitle>
        <CardDescription>{channelDescription}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <h3 className="font-medium mb-2 text-sm">Add Member</h3>
          <div className="flex items-center gap-2">
            <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="w-[300px] justify-between">
                  {selectedUser ? (selectedUser.full_name || selectedUser.username) : "Select a user..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[300px] p-0">
                <Command>
                  <CommandInput placeholder="Search user..." />
                  <CommandList>
                    <CommandEmpty>No user found.</CommandEmpty>
                    <CommandGroup>
                      {allUsers.filter(user => !members.some(m => m.user_id === user.id)).map((user) => (
                        <CommandItem key={user.id} value={`${user.full_name || ''} ${user.username || ''}`.toLowerCase()} onSelect={() => { setSelectedUser(user); setPopoverOpen(false); }}>
                          <Check className={cn("mr-2 h-4 w-4", selectedUser?.id === user.id ? "opacity-100" : "opacity-0")} />
                          {user.full_name || user.username || 'Unknown User'}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <Button onClick={handleAddUser} disabled={!selectedUser}><UserPlus className="h-4 w-4 mr-2" />Add</Button>
          </div>
        </div>
        <div>
          <h3 className="font-medium mb-2 text-sm">Current Members</h3>
          <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
            {members.length > 0 ? members.map(member => (
              <div key={member.user_id} className="flex items-center justify-between p-2 rounded-md border">
                <div className="flex items-center gap-3">
                  <Avatar className="h-8 w-8"><AvatarImage src={member.profiles?.avatar_url || undefined} /><AvatarFallback>{member.profiles?.full_name?.charAt(0) || 'U'}</AvatarFallback></Avatar>
                  <span className="text-sm">{member.profiles?.full_name || 'Unknown User'}</span>
                </div>
                <Button variant="ghost" size="icon" onClick={() => handleRemoveUser(member.user_id)}><Trash2 className="h-4 w-4 text-destructive hover:text-white" /></Button>
              </div>
            )) : <p className="text-sm text-muted-foreground text-center py-4">No users have access yet.</p>}
          </div>
        </div>
        <div className="border-t pt-6">
          <h3 className="font-medium mb-2 text-sm">Channel Features</h3>
          <div className="flex items-center justify-between p-2 rounded-md border">
            <Label htmlFor={`show-user-list-${channelSlug}`} className="text-sm font-medium flex flex-col gap-1">
              <span>Show Member List</span>
              <span className="text-xs font-normal text-muted-foreground">Display the list of online and offline members in the channel.</span>
            </Label>
            <Switch id={`show-user-list-${channelSlug}`} checked={showUserList} onCheckedChange={handleToggleUserList} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ChannelManagementCard;